import { useLazyQuery, useMutation } from '@apollo/client';
import { EditOutlined } from '@ant-design/icons';
import { Button, Form, Tooltip } from 'antd';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import CommonTable from '../../../../../components/CommonTable';
import SearchComponent from '../../../../../components/SearchComponent';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  DYNAMIC_CONFIG_TYPES,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  SORT,
} from '../../../../../common/constants';
import { formatDate, hasPermission } from '../../../../../common/utils';
import { DYNAMIC_CONFIG_ITEMS } from '../../graphql/Queries';
import { UPDATE_DYNAMIC_CONFIG_ITEM } from '../../graphql/Mutations';
import ConfigRankingModal from './ConfigRankingModal';
import ConfigRankingDescModal from './ConfigRankingDescModal';

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialSort = {
  field: 'createdAt',
  order: 'DESC',
};

const ConfigRankingTable = ({ configSubtype }) => {
  const {
    state: { permissions },
  } = useContext(AppContext);

  const [form] = Form.useForm();

  const [itemsList, setItemsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [isDistModalOpen, setIsDistModalOpen] = useState(false);
  const [isDescModalOpen, setIsDescModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const [updateConfig] = useMutation(UPDATE_DYNAMIC_CONFIG_ITEM, {
    onError: () => {},
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.CONFIGS,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  const [fetchDynamicConfigItems] = useLazyQuery(DYNAMIC_CONFIG_ITEMS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const rows = data?.dynamicConfigItems?.data;
      setItemsList(Array.isArray(rows) ? rows : []);
      const total = data?.dynamicConfigItems?.count ?? 0;
      setPaginationProp((prev) => ({ ...prev, total, current: prev?.current }));
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
      setItemsList([]);
    },
  });

  const buildSort = (sorter) =>
    sorter?.column
      ? {
          field: sorter?.field,
          order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
        }
      : initialSort;

  const fetchConfigs = ({
    skip = (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
    limit = paginationProp?.pageSize || LIMIT,
    search = searchTerm,
    sorter = sortedInfo,
  } = {}) => {
    setIsLoading(true);
    fetchDynamicConfigItems({
      variables: {
        filter: {
          configType: DYNAMIC_CONFIG_TYPES?.TOYS?.value,
          configSubtypes: configSubtype ? [configSubtype] : [],
          skip,
          limit,
          search: search?.trim() || null,
          isActive: true,
        },
        sort: buildSort(sorter),
      },
    });
  };

  useEffect(() => {
    setPaginationProp((prev) => ({ ...prev, current: 1 }));
    fetchConfigs({
      skip: 0,
      limit: paginationProp?.pageSize || LIMIT,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSubtype]);

  const handleTableChange = (pagination, _tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp((prev) => ({
      ...prev,
      ...pagination,
      current,
      pageSize: pagination?.pageSize || prev?.pageSize,
    }));
    setSortedInfo(sorter);
    fetchConfigs({ skip, limit: pagination?.pageSize || LIMIT, sorter });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setSearchTerm(trimValue);
    setPaginationProp((prev) => ({ ...prev, current: 1, skip: 0 }));
    fetchConfigs({ skip: 0, search: trimValue });
  };

  const handleDistributionSave = async (changes) => {
    try {
      setIsActionLoading(true);

      const originalMap = {};
      itemsList.forEach((item) => {
        originalMap[item.id] = item.value;
      });

      const updatedItems = changes.filter(
        (change) => String(change.newValue) !== String(originalMap[change.id]),
      );

      await Promise.all(
        updatedItems.map((change) =>
          updateConfig({
            variables: {
              where: {
                id: change.id,
                configSubtype: change.configSubtype,
                configType: change.configType,
              },
              data: {
                description: change.description,
                itemKey: change.itemKey,
                value: String(change.newValue),
                isActive: change.isActive,
              },
            },
          }),
        ),
      );

      setIsDistModalOpen(false);
      setIsActionLoading(false);
      setPaginationProp((prev) => ({ ...prev, current: 1 }));
      fetchConfigs({ skip: 0, limit: paginationProp?.pageSize || LIMIT });
    } catch {
      setIsActionLoading(false);
    }
  };

  const handleDescSave = async (values) => {
    if (!currentItem?.id) return;
    try {
      setIsActionLoading(true);

      const response = await updateConfig({
        variables: {
          where: {
            id: currentItem?.id,
            configSubtype: currentItem?.configSubtype,
            configType: currentItem?.configType,
          },
          data: {
            description: values?.description?.trim(),
            itemKey: currentItem?.itemKey,
            value: currentItem?.value,
            isActive: currentItem?.isActive,
          },
        },
      });

      if (!response?.data?.updateDynamicConfigItem?.data?.id) {
        throw new Error(
          response?.data?.updateDynamicConfigItem?.message ||
            'Unable to update description. Please try again.',
        );
      }

      setIsDescModalOpen(false);
      setCurrentItem(null);
      setIsActionLoading(false);
      setPaginationProp((prev) => ({ ...prev, current: 1 }));
      fetchConfigs({ skip: 0, limit: paginationProp?.pageSize || LIMIT });
    } catch {
      setIsActionLoading(false);
    }
  };


  const columns = useMemo(
    () => [
      {
        title: 'CATEGORY',
        dataIndex: 'itemKey',
        key: 'itemKey',
        ellipsis: true,
        width: 220,
        align: 'left',
        sorter: true,
        render: (_, record) => record?.itemKey || '-',
      },
      {
        title: 'VALUE (%)',
        dataIndex: 'value',
        key: 'value',
        ellipsis: true,
        width: 140,
        align: 'left',
        render: (_, record) =>
          record?.value != null ? `${parseFloat(record.value)}%` : '-',
      },
      {
        title: 'DESCRIPTION',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
        className: 'table-tooltip-cell',
        render: (_, record) => (
          <Tooltip
            title={record?.description ? record?.description : 'No Description'}
            placement="topLeft"
            trigger="click"
            className="pointer"
          >
            {record?.description || '-'}
          </Tooltip>
        ),
      },
      {
        title: 'CREATED AT',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        align: 'left',
        ellipsis: true,
        sorter: true,
        render: (_, record) =>
          formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      },
      {
        title: 'ACTIONS',
        dataIndex: 'actions',
        key: 'actions',
        width: 180,
        align: 'left',
        // eslint-disable-next-line no-undef
        fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
        render: (_, record) => (
          <div className="action-button">
            {hasUpdatePermission && (
              <Tooltip title="Edit Description" placement="top" zIndex={4}>
                <EditIcon
                  className="mr-16 pointer svg-icon"
                  onClick={() => {
                    setCurrentItem(record);
                    setIsDescModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [hasUpdatePermission],
  );

  const locale = {
    emptyText: isLoading ? <span /> : 'No data found',
  };

  return (
    <>
      <ConfigRankingDescModal
        form={form}
        item={currentItem}
        loadings={isActionLoading}
        isModalOpen={isDescModalOpen}
        setIsModalOpen={setIsDescModalOpen}
        onClose={() => setCurrentItem(null)}
        onFinish={handleDescSave}
      />

      <ConfigRankingModal
        isModalOpen={isDistModalOpen}
        setIsModalOpen={setIsDistModalOpen}
        items={itemsList}
        onFinish={handleDistributionSave}
        loadings={isActionLoading}
      />

      <div className="d-flex justify-end mb-16 flex-wrap" style={{ gap: 12 }}>
        {hasUpdatePermission && itemsList.length > 0 && (
          <Button
            className="common-button"
            size="middle"
            icon={<EditOutlined />}
            onClick={() => setIsDistModalOpen(true)}
          >
            Edit Distribution
          </Button>
        )}

        <SearchComponent
          className="search-box"
          query={searchTerm}
          setQuery={setSearchTerm}
          getData={handleSearch}
          name="Item"
        />
      </div>

      <CommonTable
        locale={locale}
        columns={columns}
        data={itemsList || []}
        loadingData={isLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default ConfigRankingTable;
